import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { colors, fonts } from "../../../styles/variables";
import Tooltip from "../Tooltip";

const CustomTabBarContainer = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row nowrap;
  justify-content: space-between;
  background-color: ${(props) => props.bgColor};
`;

const TabBarComponent = styled.button`
  flex: 1;
  align-items: center;
  justify-content: center;
  border: ${(props) =>
    props.isSelected ? `0px solid ${colors.black}` : `0px solid ${props.borderColor || colors.silver}`};
  border-bottom-width: 2px;
  padding: 11px 0;
  display: flex;
  flex-direction: row;
`;

const CurvedTabBarComponent = styled.button`
  flex: 1;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  padding: 7px 0;
  background-color: ${(props) => (props.isSelected ? props.selectedBgColor : colors.white)};
  z-index: ${(props) => (props.isSelected ? 2 : 1)};
  cursor: pointer;
  &:not(:last-child) {
    margin-right: -25px;
  }
  display: flex;
  flex-direction: row;
`;

const TabBarText = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${(props) => (props.isSelected ? fonts.fontInterstateUltraBlack : fonts.fontConceptRegular)};
  color: ${(props) => (props.isSelected ? props.selectedTextColor : props.deselectedTextColor)};
  text-transform: ${(props) => (props.isSelected ? "uppercase" : "none")};
  font-size: ${(props) => props.textSize};
  padding: 2px;
  letter-spacing: ${(props) => (props.isSelected ? "1.2px" : "0.2px")};
  cursor: pointer;
`;

const CustomTabBar = ({
  firstTabLabel,
  secondTabLabel,
  toolTipTitleHigh = "",
  toolTipTitleLow = "",
  isFirstTabSelected,
  onFirstTabClick,
  onSecondTabClick,
  bgColor = colors.white,
  textSize = "14px",
  isCurvedEdges = false,
  selectedBgColor = colors.black,
  selectedTextColor = colors.black,
  deselectedTextColor = colors.black,
  borderColor = colors.silver,
}) => {
  const renderTabBarComponent = (isFirstTab, tabLabel) => {
    const WrapperComponent = isCurvedEdges ? CurvedTabBarComponent : TabBarComponent;
    const onClickHandler = isFirstTab ? onFirstTabClick : onSecondTabClick;
    const isTabSelected = isFirstTab ? isFirstTabSelected : !isFirstTabSelected;
    const shouldShowTooltip = toolTipTitleHigh && toolTipTitleLow;

    return (
      <WrapperComponent
        isSelected={isTabSelected}
        onClick={() => onClickHandler()}
        selectedBgColor={selectedBgColor}
        borderColor={borderColor}
      >
        <TabBarText
          isSelected={isTabSelected}
          textSize={textSize}
          selectedTextColor={selectedTextColor}
          deselectedTextColor={deselectedTextColor}
        >
          {tabLabel}
        </TabBarText>
        {shouldShowTooltip && (
          <Tooltip
            tootipTitle={isFirstTab ? toolTipTitleHigh : toolTipTitleLow}
            isSmall
            addMarginRight={isTabSelected}
          />
        )}
      </WrapperComponent>
    );
  };

  const isFirstTab = true;
  return (
    <CustomTabBarContainer bgColor={bgColor}>
      {renderTabBarComponent(isFirstTab, firstTabLabel)}
      {renderTabBarComponent(!isFirstTab, secondTabLabel)}
    </CustomTabBarContainer>
  );
};

CustomTabBar.propTypes = {
  firstTabLabel: PropTypes.string.isRequired,
  secondTabLabel: PropTypes.string.isRequired,
  toolTipTitleHigh: PropTypes.string,
  toolTipTitleLow: PropTypes.string,
  isFirstTabSelected: PropTypes.bool.isRequired,
  onFirstTabClick: PropTypes.func.isRequired,
  onSecondTabClick: PropTypes.func.isRequired,
  bgColor: PropTypes.string,
  isCurvedEdges: PropTypes.bool,
  textSize: PropTypes.string,
  selectedTextColor: PropTypes.string,
  deselectedTextColor: PropTypes.string,
  selectedBgColor: PropTypes.string,
  borderColor: PropTypes.string,
};

export default CustomTabBar;
